import Export16 from '@carbon/icons-react/es/export/16';
import { ColumnApi, GridApi, GridReadyEvent } from 'ag-grid-community';
import { useBoolean } from 'ahooks';
import { Tooltip } from 'antd';
import { debounce, keyBy } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { ExportQueueStatus } from 'api/async-export';
import { OrderType, ProjectId } from 'api/models';
import { Project, ProjectDetailTabKeys } from 'api/project';
import { Button } from 'components/atoms/button';
import { Column, Selectable } from 'components/atoms/grid';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import {
  HideForCustomer,
  HideForVendor,
} from 'components/atoms/user-type-utils';
import { BreadCrumbs } from 'components/molecules/project-details/header/breadcrumbs';
import { ProjectTitle } from 'components/molecules/project-details/header/project-title';
import { ProjectDetailTabs } from 'components/molecules/project-details/header/tabs/tabs';
import { SidebarCollapseButton } from 'components/molecules/sidebar-collapse-button/SidebarCollapseButton';
import { toastService } from 'components/molecules/toast';
import { useCurrentView } from 'components/molecules/views/current-view-state';
import { FilterMenuProject } from 'components/organisms/filter-menu-sidebar';
import {
  asColumnOrder,
  cleanUpColumnOrder,
  ColumnListPopover,
  ColumnOrder,
  GridChangeEvent,
} from 'components/organisms/grid-custom-column';
import { TaskList } from 'components/organisms/tasks';
import { COLUMN_ORDER_SERIALIZER } from 'components/organisms/tasks/column-order-serializer';
import { FilterContainer } from 'components/organisms/tasks/components';
import { exportAsCSV } from 'components/organisms/tasks/export';
import {
  SingleSelectField,
  TextField,
} from 'components/organisms/tasks/fields';
import { TaskFilterView } from 'components/organisms/tasks/filter-views';
import {
  getDefaultColumnOrder,
  useProjectTaskColumns,
} from 'components/organisms/tasks/use-columns';
import { useFilters } from 'components/organisms/tasks/use-filters';
import {
  default_column_order,
  DEFAULT_COLUMN_ORDER,
  stringToColumnOrder,
} from 'components/organisms/tasks/utils';
import { ProjectTasksViewSwitcher } from 'components/pages/task-list/project-tasks-view-switcher';
import { useProjectTaskListFilter } from 'components/pages/task-list/use-project-task-list-filter';
import { Neon } from 'components/templates/neon';
import { TOGGLE_PROJECTS_SIDEBAR } from 'constant/constant';
import { RootState } from 'data';
import { useProjectPhaseByProjectId } from 'data/projects';
import { useAccount } from 'hooks/use-account';
import { useCustomerPreview } from 'hooks/use-customer-preview';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import {
  addUpdateColumnOrderIndexedDb,
  getColumnOrderFromIndexedDb,
} from 'hooks/use-idb';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslate } from 'hooks/use-translation';
import { useIsExternalUser } from 'hooks/use-user-type';
import { useProjectWebsocket } from 'hooks/websockets';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { Empty } from 'stores/core';
import { FieldsStore, TaskField } from 'stores/fields';
import { withValidCustomTaskFields } from 'utils/duplicate-fields-transformer';
import { getRandomColor } from 'utils/get-random-color';
import { SelectionControls } from '../selection-controls';
import { useProjectTasksRefresh } from '../use-project-tasks-refresh';
import { useProjectTasksSocketUpdates } from '../use-project-tasks-socket-updates';
import { useSelections } from '../use-selections';

const excludedTaskFields = [
  'taskId',
  'taskDescription',
  'isCsatEnabled',
  'project',
  'privateTaskDescription',
];

const EMPTY_RECORD = {};
const excludedColumnsForReordering = ['checkbox', 'status'];

export const ProjectTaskListPage: FC<{
  project: Project;
}> = ({ project }) => {
  const { projectId } = project;
  const { accountId } = useAccount();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const { data: jiraIntegrationConfig } = useQueryJiraConfig();
  const phases = useProjectPhaseByProjectId(projectId);
  const { isPreview } = useCustomerPreview();
  const isExternalUser = useIsExternalUser();

  const excludedFields = useMemo(() => {
    // remove jiraIntegration from excluded fields ONLY when the integration is active
    if (jiraIntegrationConfig?.length === 0) {
      return [...excludedTaskFields, 'jiraIntegration'];
    }

    return excludedTaskFields;
  }, [jiraIntegrationConfig]);

  const excludedFieldsForColumns = useMemo(
    () => [
      ...excludedFields,
      'private',
      'completedLateBy',
      'phaseName',
      'taskDependencyStatus',
    ],
    [excludedFields],
  );

  const excludedFieldsForFilters = useMemo(
    () => [...excludedFields, 'phaseName'],
    [excludedFields],
  );

  const [fields, fieldsForFilter] = useMemo(() => {
    const taskFields = FieldsStore.enabledTaskFields(fieldsStore).map((field) =>
      withValidCustomTaskFields(accountId, field),
    );

    return [
      taskFields.filter(
        ({ fieldName }) => !excludedFieldsForColumns.includes(fieldName),
      ),
      taskFields
        .filter(
          ({ fieldName }) => !excludedFieldsForFilters.includes(fieldName),
        )
        .map((field) => {
          return field.fieldName === 'projectPhaseId'
            ? ({
                ...field,
                metaData: {
                  ...field.metaData,
                  options: phases.map((phase) => ({
                    active: true,
                    label: phase?.projectPhaseName,
                    value: phase?.projectPhaseId,
                    color: getRandomColor(phase?.projectPhaseName || ''),
                  })),
                },
              } as TaskField)
            : field;
        }),
    ];
  }, [
    accountId,
    excludedFieldsForColumns,
    excludedFieldsForFilters,
    fieldsStore,
    phases,
  ]);

  const fieldsLookupById = useMemo(
    () => keyBy(fields, 'fieldId') as Record<number, TaskField>,
    [fields],
  );

  const fieldsLookupByName = useMemo(
    () =>
      fieldsForFilter.length > 0
        ? (keyBy(fieldsForFilter, 'fieldName') as Record<string, TaskField>)
        : EMPTY_RECORD,
    [fieldsForFilter],
  );

  const fieldsLookupByNameForColumns = useMemo(
    () => keyBy(fields, 'fieldName') as Record<string, TaskField>,
    [fields],
  );

  const fields_loading = !FieldsStore.loaded(fieldsStore);

  const { byProjectId } = useSelector(({ tasks_v2 }: RootState) => tasks_v2);
  const taskIds = useMemo(
    () => byProjectId[projectId] ?? Empty(),
    [byProjectId, projectId],
  );
  const status = taskIds[0];
  const { filter, filterFunction, clearFilters, selectFilter, replaceFilter } =
    useFilters(fieldsLookupByName, status === 'refreshing' ? 'loaded' : status);

  const [filterInitialized, setFilterInitialized] = useState(false);
  const projectField = FieldsStore.getTaskFieldByName('project')(
    fieldsStore,
  )[1] as (SingleSelectField & TaskField) | undefined;
  const taskNameField = FieldsStore.getTaskFieldByName('taskName')(
    fieldsStore,
  )[1] as (TextField & TaskField) | undefined;
  const selectableRef = useRef<Selectable>(null);
  const { selected, setSelected, reset, selectAll, count } =
    useSelections(selectableRef);
  const isCustomerPortal = isPreview || isExternalUser;

  useProjectTaskListFilter({
    localStorageKey: `task_list.query.project_id-${projectId}`,
    taskStatus: status,
    replaceFilter,
    filter,
    fieldsLoading: fields_loading,
    fieldLookup: fieldsLookupByName,
    filterInitialized,
    setFilterInitialized,
  });

  const [
    isExportInProgress,
    { setTrue: setExportStarted, setFalse: setExportEnded },
  ] = useBoolean(false);

  const translate = useTranslate();

  useProjectTasksRefresh({
    projectId: ProjectId(projectId),
    fieldTransformer: useCallback(
      (field) => {
        return withValidCustomTaskFields(accountId, field) as TaskField;
      },
      [accountId],
    ),
  });
  useProjectTasksSocketUpdates(
    ProjectId(projectId),
    setExportEnded,
    isExportInProgress,
  );
  useProjectWebsocket(ProjectId(projectId));

  const [columnOrderV2] = useLocalStorage<string[]>(
    `project_${projectId}.task_list.column_order`,
    default_column_order,
    COLUMN_ORDER_SERIALIZER,
  );
  const { viewId: currentViewId } = useCurrentView({
    defaultViewId: -1,
  });
  const masterColumnOrder = useMemo(
    () => columnOrderV2.map(stringToColumnOrder) || DEFAULT_COLUMN_ORDER,
    [columnOrderV2],
  );

  const columns: Column[] = useProjectTaskColumns(fieldsLookupById, translate);

  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] =
    useState<ColumnApi | undefined>(undefined);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
    setGridColumnApi(event.columnApi);
  }, []);

  const defaultColumnState: ColumnOrder[] = useMemo(
    () => getDefaultColumnOrder(columns),
    [columns],
  );

  const [order, setOrder] = useState(
    cleanUpColumnOrder(defaultColumnState)(masterColumnOrder),
  );

  useEffect(() => {
    async function getColOrder() {
      const lsOrder = JSON.parse(
        localStorage.getItem(
          `project_${projectId}.task_list.v3.view=${currentViewId}column_order`,
        ) || '[]',
      );
      const idbData = await getColumnOrderFromIndexedDb(
        `project_${projectId}.task_list.v1.view=${currentViewId}`,
      );
      let data =
        idbData && idbData.columnOrder.length > 0 ? idbData.columnOrder : [];

      if (lsOrder && lsOrder.length > 0) {
        data = lsOrder;
        localStorage.removeItem(
          `project_${projectId}.task_list.v3.view=${currentViewId}column_order`,
        );
      }

      if (data && data?.length > 0) {
        if (defaultColumnState && defaultColumnState.length > 0) {
          setOrder(cleanUpColumnOrder(defaultColumnState)(data));
        } else {
          setOrder(data);
        }
      }
    }
    getColOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewId, projectId]);

  const [columnOrder, setColumnOrder] = useState<ColumnOrder[]>(order);

  const exportAsCsvWithToast = () => {
    setExportStarted();
    exportAsCSV({
      filter,
      projectId,
      columnOrder,
      lookup: fieldsLookupByNameForColumns,
      failExport: setExportEnded,
    })
      .then((exportStatus) => {
        if (exportStatus === ExportQueueStatus.Submitted) {
          toastService({
            title: translate('async.exports.in_progress.title'),
            message: translate('async.exports.in_progress.message'),
          }).success();
        }
      })
      .catch(() => {
        toastService({
          title: translate('async.exports.error.title'),
          message: translate('async.exports.error.message'),
        }).error();
      });
  };

  const blockEffect = useRef(false);

  const handleColumnOrderChangedByView = useCallback(
    (changedColumnOrder: ColumnOrder[]) => {
      blockEffect.current = true;
      setColumnOrder(
        cleanUpColumnOrder(defaultColumnState)(changedColumnOrder),
      );
    },
    [defaultColumnState],
  );

  useEffect(() => {
    if (blockEffect.current) {
      return;
    }
    setColumnOrder(order);
  }, [currentViewId, order]);

  useEffect(() => {
    // Used to update the grid with latest columnOrder
    if (!gridColumnApi) {
      return;
    }
    gridColumnApi?.setColumnState(columnOrder);
  }, [columnOrder, gridColumnApi]);

  useEffect(() => {
    async function addUpdateColOrder() {
      await addUpdateColumnOrderIndexedDb(
        `project_${projectId}.task_list.v1.view=${currentViewId}`,
        currentViewId,
        order,
        OrderType.ProjectTaskList,
      );
    }
    addUpdateColOrder();
  }, [currentViewId, order, projectId]);

  const handleGridChange = useCallback(
    (event: GridChangeEvent) => {
      if (event) {
        const newColumnOrder = asColumnOrder(event.columnApi.getColumnState());

        setColumnOrder(newColumnOrder);
        setOrder(newColumnOrder);
      }
    },
    [setOrder],
  );

  const throttledGridChange = useMemo(
    () => debounce(handleGridChange, 100),
    [handleGridChange],
  );

  const { active, exit, enter } = useDistractionFreeMode(
    TOGGLE_PROJECTS_SIDEBAR,
  );

  function sidebarToggle() {
    if (active) {
      exit();
    } else {
      enter();
    }
  }

  return (
    <Neon
      isDistractionFree={active}
      title={<ProjectTitle project={project} />}
      tabs={
        <HideForCustomer>
          <ProjectDetailTabs
            projectId={ProjectId(projectId)}
            activeTabKey={ProjectDetailTabKeys.TaskList}
          />
        </HideForCustomer>
      }
      sider={
        !isCustomerPortal ? (
          <FilterMenuProject
            fields={fields}
            lookup={fieldsLookupByName}
            filter={filter}
            selectFilter={selectFilter}
            clearFilters={clearFilters}
            isFieldsLoading={fields_loading}
            columnOrder={columnOrder}
            handleColumnOrderChange={handleColumnOrderChangedByView}
            defaultColumnOrder={defaultColumnState}
            masterColumnOrder={masterColumnOrder}
          />
        ) : null
      }
      breadcrumbs={<BreadCrumbs />}
      actions={
        count > 0
          ? {
              primary: (
                <SelectionControls
                  count={count}
                  reset={reset}
                  selectAll={selectAll}
                />
              ),
            }
          : {
              primary: (
                <FilterContainer>
                  <SidebarCollapseButton sidebarToggle={sidebarToggle} />
                  <TaskFilterView
                    isFieldsLoading={fields_loading}
                    filter={filter}
                    fields={fieldsForFilter}
                    selectFilter={selectFilter}
                    clearFilters={clearFilters}
                    lookup={fieldsLookupByName}
                    includeSavedViews={!!isCustomerPortal}
                    columnOrder={columnOrder}
                    defaultColumnOrder={defaultColumnState}
                    handleColumnOrderChange={handleColumnOrderChangedByView}
                  />
                </FilterContainer>
              ),
              secondary: (
                <>
                  <ColumnListPopover
                    columnOrder={columnOrder}
                    gridColumnApi={gridColumnApi}
                    excludedColumns={excludedColumnsForReordering}
                    defaultColumnOrder={default_column_order}
                  />
                  <Tooltip
                    title={<FormattedMessage id="export.tooltip" />}
                    placement="topLeft"
                  >
                    <Button
                      icon={
                        <IconWrapper>
                          <Export16 />
                        </IconWrapper>
                      }
                      type="text"
                      size="small"
                      loading={isExportInProgress}
                      onClick={exportAsCsvWithToast}
                    />
                  </Tooltip>
                </>
              ),
              central: (
                <HideForVendor>
                  <ProjectTasksViewSwitcher projectId={ProjectId(projectId)} />
                </HideForVendor>
              ),
            }
      }
      content={
        <TaskList
          fields={fieldsLookupById}
          projectId={projectId}
          filterFunction={filterFunction}
          initialized={filterInitialized}
          projectField={projectField}
          taskNameField={taskNameField}
          selected={selected}
          onSelectionChange={setSelected}
          selectableRef={selectableRef}
          gridApi={gridApi}
          onGridReady={onGridReady}
          handleGridChange={throttledGridChange}
          columns={columns}
        />
      }
    />
  );
};
